import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { seedCases, seedCustomers } from '../data/adminSeed';
import { company } from '../data/site';
import { loadState, saveState } from '../utils/storage';
import { makeCredential, verifyCredential } from '../utils/password';
import type { Credential } from '../utils/password';
import type { AdminNotification, AdminSettings, Case, CasePhoto, CaseStatus, Customer, EnquiryInput, NewCaseInput, PhotoStage } from '../types/admin';
const defaultSettings: AdminSettings = {
  companyName: company.name,
  tagline: company.tagline,
  cvr: company.cvr,
  phone: company.phone,
  email: company.email,
  address: '',
  serviceArea: 'Storkøbenhavn og omegn',
  website: 'lykkevaerk.dk',
  openingHours: 'Man–fre 07.00–16.00',
  hourlyRate: '460',
  callOutFee: '0'
};
export type PasswordChangeResult = 'ok' | 'wrong-current' | 'mismatch' | 'too-short' | 'save-failed';
interface AdminData {
  loggedIn: boolean;
  login: () => void;
  logout: () => void;
  /** Kontrollerer adgangskoden mod den gemte hash. */
  verifyPassword: (password: string) => Promise<boolean>;
  /** Skifter adgangskode. Den nuværende skal være korrekt. */
  changePassword: (current: string, next: string, confirm: string) => Promise<PasswordChangeResult>;
  cases: Case[];
  customers: Customer[];
  notifications: AdminNotification[];
  settings: AdminSettings;
  /** True hvis browseren afviste at gemme (fx fuld lagring). */
  storageError: boolean;
  customerById: (id: string) => Customer | undefined;
  casesForCustomer: (customerId: string) => Case[];
  createCase: (input: NewCaseInput) => Case;
  createEnquiry: (input: EnquiryInput) => void;
  createCustomer: (customer: Omit<Customer, 'id'>) => Customer;
  updateCustomer: (id: string, patch: Partial<Customer>) => void;
  deleteCustomer: (id: string) => void;
  updateCase: (id: string, patch: Partial<Case>) => void;
  deleteCase: (id: string) => void;
  setStatus: (id: string, status: CaseStatus) => void;
  addNote: (id: string, text: string) => void;
  deleteNote: (caseId: string, noteId: string) => void;
  addPhotos: (id: string, photos: {
    url: string;
    name: string;
  }[], stage: PhotoStage) => void;
  deletePhoto: (caseId: string, photoId: string) => void;
  /** Gemmer indstillinger med det samme. Returnerer false hvis lagringen fejlede. */
  saveSettings: (next: AdminSettings) => boolean;
  markNotificationsRead: () => void;
}
const AdminDataContext = createContext<AdminData | null>(null);
function nextNumber(cases: Case[]): string {
  const highest = cases.reduce((max, item) => Math.max(max, Number(item.number)), 0);
  return String(highest + 1).padStart(4, '0');
}
function uid(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}
export function AdminDataProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [loggedIn, setLoggedIn] = useState<boolean>(() => loadState('session', false));
  const [cases, setCases] = useState<Case[]>(() => loadState('cases', seedCases));
  const [customers, setCustomers] = useState<Customer[]>(() => loadState('customers', seedCustomers));
  const [notifications, setNotifications] = useState<AdminNotification[]>(() => loadState<AdminNotification[]>('notifications', []));
  const [settings, setSettings] = useState<AdminSettings>(() => ({
    ...defaultSettings,
    ...loadState<Partial<AdminSettings>>('settings', {})
  }));
  const [storageError, setStorageError] = useState(false);

  /** Henter (eller opretter første gang) den gemte hash af adgangskoden. */
  const getCredential = useCallback(async (): Promise<Credential> => {
    const stored = loadState<Credential | null>('auth', null);
    if (stored && stored.hash && stored.salt) return stored;
    const created = await makeCredential(company.defaultAdminPassword);
    saveState('auth', created);
    return created;
  }, []);
  const verifyPassword = useCallback(async (password: string) => {
    const credential = await getCredential();
    return verifyCredential(password, credential);
  }, [getCredential]);
  const changePassword = useCallback(async (current: string, next: string, confirm: string): Promise<PasswordChangeResult> => {
    const credential = await getCredential();
    const currentOk = await verifyCredential(current, credential);
    if (!currentOk) return 'wrong-current';
    if (next.length < 8) return 'too-short';
    if (next !== confirm) return 'mismatch';
    const updated = await makeCredential(next);
    if (!saveState('auth', updated)) return 'save-failed';
    return 'ok';
  }, [getCredential]);
  useEffect(() => {
    saveState('session', loggedIn);
  }, [loggedIn]);
  useEffect(() => {
    if (!saveState('cases', cases)) setStorageError(true);
  }, [cases]);
  useEffect(() => {
    if (!saveState('customers', customers)) setStorageError(true);
  }, [customers]);
  useEffect(() => {
    if (!saveState('notifications', notifications)) setStorageError(true);
  }, [notifications]);
  const customerById = useCallback((id: string) => customers.find(customer => customer.id === id), [customers]);
  const casesForCustomer = useCallback((customerId: string) => cases.filter(item => item.customerId === customerId), [cases]);
  const createCustomer = useCallback((customer: Omit<Customer, 'id'>) => {
    const created: Customer = {
      ...customer,
      id: uid('cust')
    };
    setCustomers(current => [...current, created]);
    return created;
  }, []);
  const updateCustomer = useCallback((id: string, patch: Partial<Customer>) => {
    setCustomers(current => current.map(customer => customer.id === id ? {
      ...customer,
      ...patch
    } : customer));
  }, []);
  const deleteCustomer = useCallback((id: string) => {
    setCustomers(current => current.filter(customer => customer.id !== id));
    setCases(current => current.filter(item => item.customerId !== id));
  }, []);
  const createCase = useCallback((input: NewCaseInput) => {
    let customerId = input.customerId;
    if (input.newCustomer) {
      const createdCustomer: Customer = {
        ...input.newCustomer,
        notes: '',
        id: uid('cust')
      };
      setCustomers(current => [...current, createdCustomer]);
      customerId = createdCustomer.id;
    }
    const photos: CasePhoto[] = input.photos.map(photo => ({
      id: uid('photo'),
      url: photo.url,
      name: photo.name,
      stage: 'Før'
    }));
    const created: Case = {
      id: uid('case'),
      number: nextNumber(cases),
      customerId,
      title: input.title,
      address: input.address,
      description: input.description,
      category: input.category,
      priceModel: input.priceModel,
      hourlyRate: input.hourlyRate,
      fixedPrice: input.fixedPrice,
      estimate: input.fixedPrice || '',
      finalAmount: '',
      plannedDate: input.plannedDate,
      status: input.status,
      internalNotes: input.internalNote ? [{
        id: uid('note'),
        text: input.internalNote,
        createdAt: new Date().toISOString()
      }] : [],
      photos,
      createdAt: new Date().toISOString(),
      fromWebsite: false
    };
    setCases(current => [created, ...current]);
    return created;
  }, [cases]);
  const createEnquiry = useCallback((input: EnquiryInput) => {
    const existing = customers.find(customer => input.email && customer.email.toLowerCase() === input.email.toLowerCase() || input.phone && customer.phone.replace(/\s/g, '') === input.phone.replace(/\s/g, ''));
    let customerId = existing?.id;
    if (!customerId) {
      const created: Customer = {
        id: uid('cust'),
        name: input.name,
        phone: input.phone,
        email: input.email,
        address: input.address,
        notes: 'Oprettet automatisk fra hjemmesiden.'
      };
      customerId = created.id;
      setCustomers(current => [...current, created]);
    }
    const newCaseId = uid('case');
    setCases(current => [{
      id: newCaseId,
      number: nextNumber(current),
      customerId: customerId as string,
      title: input.description.split(/[.\n]/)[0].slice(0, 60) || 'Henvendelse fra hjemmesiden',
      address: input.address,
      description: input.description,
      category: 'Multiservice',
      priceModel: 'Tilbud endnu ikke lavet',
      hourlyRate: '',
      fixedPrice: '',
      estimate: '',
      finalAmount: '',
      plannedDate: '',
      status: 'Ny henvendelse',
      internalNotes: [],
      photos: input.photos.map(photo => ({
        id: uid('photo'),
        url: photo.url,
        name: photo.name,
        stage: 'Før' as PhotoStage
      })),
      createdAt: new Date().toISOString(),
      fromWebsite: true
    }, ...current]);
    setNotifications(current => [{
      id: uid('noti'),
      caseId: newCaseId,
      customerName: input.name,
      createdAt: new Date().toISOString(),
      read: false
    }, ...current]);
  }, [customers]);
  const updateCase = useCallback((id: string, patch: Partial<Case>) => {
    setCases(current => current.map(item => item.id === id ? {
      ...item,
      ...patch
    } : item));
  }, []);
  const deleteCase = useCallback((id: string) => {
    setCases(current => current.filter(item => item.id !== id));
    setNotifications(current => current.filter(item => item.caseId !== id));
  }, []);
  const setStatus = useCallback((id: string, status: CaseStatus) => {
    setCases(current => current.map(item => item.id === id ? {
      ...item,
      status
    } : item));
  }, []);
  const addNote = useCallback((id: string, text: string) => {
    setCases(current => current.map(item => item.id === id ? {
      ...item,
      internalNotes: [{
        id: uid('note'),
        text,
        createdAt: new Date().toISOString()
      }, ...item.internalNotes]
    } : item));
  }, []);
  const deleteNote = useCallback((caseId: string, noteId: string) => {
    setCases(current => current.map(item => item.id === caseId ? {
      ...item,
      internalNotes: item.internalNotes.filter(note => note.id !== noteId)
    } : item));
  }, []);
  const addPhotos = useCallback((id: string, photos: {
    url: string;
    name: string;
  }[], stage: PhotoStage) => {
    setCases(current => current.map(item => item.id === id ? {
      ...item,
      photos: [...item.photos, ...photos.map(photo => ({
        id: uid('photo'),
        url: photo.url,
        name: photo.name,
        stage
      }))]
    } : item));
  }, []);
  const deletePhoto = useCallback((caseId: string, photoId: string) => {
    setCases(current => current.map(item => item.id === caseId ? {
      ...item,
      photos: item.photos.filter(photo => photo.id !== photoId)
    } : item));
  }, []);
  const saveSettings = useCallback((next: AdminSettings) => {
    const ok = saveState('settings', next);
    if (ok) {
      setSettings(next);
      setStorageError(false);
    } else {
      setStorageError(true);
    }
    return ok;
  }, []);
  const markNotificationsRead = useCallback(() => {
    setNotifications(current => current.map(item => ({
      ...item,
      read: true
    })));
  }, []);
  const value = useMemo<AdminData>(() => ({
    loggedIn,
    login: () => setLoggedIn(true),
    logout: () => setLoggedIn(false),
    verifyPassword,
    changePassword,
    cases,
    customers,
    notifications,
    settings,
    storageError,
    customerById,
    casesForCustomer,
    createCase,
    createEnquiry,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    updateCase,
    deleteCase,
    setStatus,
    addNote,
    deleteNote,
    addPhotos,
    deletePhoto,
    saveSettings,
    markNotificationsRead
  }), [loggedIn, verifyPassword, changePassword, cases, customers, notifications, settings, storageError, customerById, casesForCustomer, createCase, createEnquiry, createCustomer, updateCustomer, deleteCustomer, updateCase, deleteCase, setStatus, addNote, deleteNote, addPhotos, deletePhoto, saveSettings, markNotificationsRead]);
  return <AdminDataContext.Provider value={value} data-id="element-215" data-mp="13klj6me6xs9q">{children}</AdminDataContext.Provider>;
}
export function useAdminData(): AdminData {
  const context = useContext(AdminDataContext);
  if (!context) {
    throw new Error('useAdminData skal bruges inde i AdminDataProvider');
  }
  return context;
}